import { NONE_FILTER } from './filters'

/**
 * Recursively freezes a value in place. Route props spread only the first level of a segment entry,
 * so the status / types arrays stay shared with the registry, and two segments can even share the
 * same array instance. Freezing turns an accidental in-place mutation into a visible error instead
 * of a silent corruption spreading to every route and to the count payloads.
 *
 * @param {*} value - value to freeze
 * @returns {*} the same value, deeply frozen
 */
function deepFreeze(value) {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) {
    return value
  }
  Object.freeze(value)
  const nestedValues = Object.values(value)
  for (const nestedValue of nestedValues) {
    deepFreeze(nestedValue)
  }

  return value
}

/**
 * The four types shared by the two "in preparation" sub-order segments, same list and same order.
 * preparation_history whitelists the same four in its own order, and that order drives the type
 * filter dropdown, so it keeps its own literal rather than reusing this one.
 */
const PREPARATION_TYPES = ['click_and_collect', 'ship_from_store', 'click_and_reserve', 'inter_store']

/**
 * Single source of truth for every menu segment: statuses plus type / carrier_type whitelists.
 * Keys match the table components' prop names so a route can spread an entry as is, and the two
 * scopes match the two count endpoints, which each key their filters payload by segment name.
 * Scoped by resource because waiting_carrier and waiting_customer exist on both sides with
 * different filters. NONE_FILTER also matches documents where the field is missing.
 */
export const SEGMENTS = deepFreeze({
  orders: {
    waiting_customer: {
      status: ['to_pickup'],
      types: ['click_and_collect', 'in_store_pickup', 'click_and_reserve', 'inter_store', NONE_FILTER],
      carrierTypes: ['store_delivery', NONE_FILTER],
    },
    waiting_payment: {
      status: ['waiting_payment'],
    },
    // Named after the API count facet, whose match is on the 'blocked' status.
    non_compliant: {
      status: ['blocked'],
    },
    error: {
      status: ['error'],
    },
  },
  // No carrierTypes here: SubOrdersTable has no such prop and the API defaults carrier_type to null.
  subOrders: {
    waiting: {
      status: ['waiting'],
      types: PREPARATION_TYPES,
    },
    confirmed: {
      status: ['confirmed', 'partially_confirmed'],
      types: PREPARATION_TYPES,
    },
    waiting_parcel_receipt: {
      status: [
        'waiting',
        'confirmed',
        'partially_confirmed',
        'shipping_in_progress',
        'partial_shipping_in_progress',
        'waiting_carrier',
      ],
      types: ['in_store_pickup', 'inter_store'],
    },
    waiting_carrier: {
      status: ['waiting_carrier'],
      types: ['ship_from_store', 'inter_store'],
    },
    waiting_allocation: {
      status: ['waiting_allocation'],
    },
    preparation_history: {
      status: [
        'delivered',
        'partially_delivered',
        'cancelled',
        'shipping_in_progress',
        'partial_shipping_in_progress',
        'to_pickup',
      ],
      types: ['inter_store', 'ship_from_store', 'click_and_collect', 'click_and_reserve'],
    },
  },
})

/**
 * Segments whose filters the count endpoints accept as an override. Shorter than the list of
 * segments carrying a badge: the other facets (non_compliant, error, waiting_allocation,
 * to_put_back_in_stock) are hard-coded matches on the API side, which silently ignores any key it
 * does not own, so sending them would only add dead weight to the payload.
 */
const FILTERABLE_ORDER_SEGMENTS = ['waiting_customer']
const FILTERABLE_SUB_ORDER_SEGMENTS = ['waiting', 'confirmed', 'waiting_parcel_receipt', 'waiting_carrier']

/**
 * Builds the `filters` payload of a count endpoint from a segment scope, renaming the
 * component-shaped `types` / `carrierTypes` keys to the `type` / `carrier_type` the API expects,
 * once instead of at every call site. A segment without a whitelist yields an undefined value,
 * which disappears on JSON.stringify, so the API falls back to its own default for that key.
 *
 * The three axes a segment is made of travel together: the count endpoints accept a `status`
 * override alongside the two whitelists, so sending only the whitelists would leave the badge
 * counting on the API default status while the list uses the registry — the same drift the
 * registry exists to prevent, moved one axis over.
 *
 * @param {Object} scope - one SEGMENTS scope, keyed by segment name
 * @param {string[]} segmentNames - segments to include in the payload
 * @returns {Object} payload keyed by segment name, each holding { carrier_type, status, type }
 * @throws {Error} when a name is missing from the scope, at import time rather than on the request
 */
function toCountFilters(scope, segmentNames) {
  const filters = {}
  for (const segmentName of segmentNames) {
    const segment = scope[segmentName]
    if (!segment) {
      throw new Error(`Unknown segment "${segmentName}": no such entry in this SEGMENTS scope`)
    }
    filters[segmentName] = { carrier_type: segment.carrierTypes, status: segment.status, type: segment.types }
  }

  return deepFreeze(filters)
}

/** `filters` payload for GET orders/count, shared with the order routes. */
export const ORDER_COUNT_FILTERS = toCountFilters(SEGMENTS.orders, FILTERABLE_ORDER_SEGMENTS)

/** `filters` payload for GET subOrders/count, shared with the sub-order routes. */
export const SUB_ORDER_COUNT_FILTERS = toCountFilters(SEGMENTS.subOrders, FILTERABLE_SUB_ORDER_SEGMENTS)
